/**
 * Manager Timesheet Approvals — bulk view of pending submissions.
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const MANAGER_ROLES = ["super_admin", "hr_admin", "manager", "department_head", "team_lead"];

export default function TimesheetApprovals() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const allowed = MANAGER_ROLES.includes(user?.role);

  useEffect(() => { if (allowed) load(); else setLoading(false); /* eslint-disable-next-line */ }, [allowed]);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/timesheets/team-pending");
      setPending(data.pending || []);
    } catch (e) { /* no-op — already gated */ }
    finally { setLoading(false); }
  };

  if (!allowed) {
    return (
      <div className="p-10 text-center">
        <Lock className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <p className="text-sm text-slate-500">Timesheet approvals are visible to managers and HR only. Your timesheet is at <a className="text-cyan-600 underline" href="/timesheets">/timesheets</a>.</p>
      </div>
    );
  }

  const approve = async (id) => {
    try { await api.post(`/timesheets/approve/${id}`); toast.success("Approved"); await load(); }
    catch (e) { toast.error("Approve failed"); }
  };
  const reject = async (id) => {
    const note = window.prompt("Reason for rejection?", "");
    if (note === null) return;
    try { await api.post(`/timesheets/reject/${id}`, { note }); toast.success("Rejected"); await load(); }
    catch (e) { toast.error("Reject failed"); }
  };

  if (loading) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 space-y-5 max-w-7xl">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Approvals</div>
        <h1 className="text-2xl font-bold font-[Outfit]">Timesheet Approvals</h1>
        <p className="text-sm text-slate-500 mt-1">Review and approve your team's submitted daily timesheets.</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-[Outfit]">Pending ({pending.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {pending.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">No timesheets awaiting approval</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left p-3">Employee</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-right p-3">Hours</th>
                  <th className="text-left p-3">Entries</th>
                  <th className="text-left p-3">Submitted</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((p) => (
                  <tr key={p.id} className="border-t" data-testid={`pending-${p.id}`}>
                    <td className="p-3 font-medium">{p.employee_name}</td>
                    <td className="p-3">{p.date}</td>
                    <td className="p-3 text-right font-mono">{p.total_hours}h</td>
                    <td className="p-3 text-xs text-slate-600">
                      {(p.entries || []).slice(0, 3).map((e, i) => <div key={i} className="truncate max-w-md">· {e.description} ({e.hours}h)</div>)}
                      {(p.entries || []).length > 3 && <div className="text-slate-400">+{p.entries.length - 3}</div>}
                    </td>
                    <td className="p-3 text-xs text-slate-400">{p.submitted_at && new Date(p.submitted_at).toLocaleDateString()}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approve(p.id)} data-testid={`approve-${p.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                      <Button size="sm" variant="outline" className="ml-1 text-rose-600" onClick={() => reject(p.id)} data-testid={`reject-${p.id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
